import { type CustomEngineDef, isWebTemplate } from './custom';
import type { Group, Preset, Settings } from './types';

const SETTINGS_KEY = 'abs:settings';
const PRESETS_KEY = 'abs:presets';
const ENGINES_KEY = 'abs:engines';
const RECENT_KEY = 'abs:recent';
const GROUPS_KEY = 'abs:groups';
const RECENT_MAX = 30;

export const DEFAULT_SETTINGS: Settings = {
  defaultBang: 'g',
  ddgFallback: true,
  keepRecent: true,
};

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

// These reads run on the redirect path, so they use cheap shape checks rather
// than Zod. Import/export in the UI validates fully. Anything the wrong type
// is dropped, so a corrupted or older store can't break every page load.

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const optString = (v: unknown) => v === undefined || typeof v === 'string';

const SETTING_TYPES: Record<keyof Settings, string> = {
  defaultBang: 'string',
  zip: 'string',
  distance: 'number',
  craigslistSite: 'string',
  marketplaceCity: 'string',
  home: 'string',
  ddgFallback: 'boolean',
  keepRecent: 'boolean',
  engineDefaults: 'object',
  engineAliases: 'object',
  shoppingLinks: 'boolean',
};

export function loadSettings(): Settings {
  const stored = read(SETTINGS_KEY);
  if (!isObj(stored)) return { ...DEFAULT_SETTINGS };
  const kept: Record<string, unknown> = {};
  for (const [key, type] of Object.entries(SETTING_TYPES)) {
    const v = stored[key];
    if (typeof v === type && v !== '') kept[key] = v;
  }
  if (kept.engineDefaults !== undefined) {
    kept.engineDefaults = isObj(kept.engineDefaults)
      ? Object.fromEntries(
          Object.entries(kept.engineDefaults).filter(
            ([, v]) => typeof v === 'string'
          )
        )
      : undefined;
  }
  if (kept.engineAliases !== undefined) {
    kept.engineAliases = isObj(kept.engineAliases)
      ? Object.fromEntries(
          Object.entries(kept.engineAliases).filter(
            ([, v]) => Array.isArray(v) && v.every(a => typeof a === 'string')
          )
        )
      : undefined;
  }
  return { ...DEFAULT_SETTINGS, ...(kept as Partial<Settings>) };
}

export function saveSettings(settings: Settings): boolean {
  return write(SETTINGS_KEY, settings);
}

export function loadPresets(): Preset[] {
  const stored = read(PRESETS_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (p): p is Preset =>
      isObj(p) &&
      typeof p.name === 'string' &&
      typeof p.engine === 'string' &&
      isObj(p.values) &&
      optString(p.terms) &&
      optString(p.description) &&
      (p.aliases === undefined ||
        (Array.isArray(p.aliases) &&
          p.aliases.every(a => typeof a === 'string')))
  );
}

export function savePresets(presets: Preset[]): boolean {
  return write(PRESETS_KEY, presets);
}

export function loadCustomEngines(): CustomEngineDef[] {
  const stored = read(ENGINES_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (e): e is CustomEngineDef =>
      isObj(e) &&
      typeof e.bang === 'string' &&
      typeof e.name === 'string' &&
      typeof e.template === 'string' &&
      isWebTemplate(e.template) &&
      optString(e.description) &&
      optString(e.category) &&
      Array.isArray(e.modifiers) &&
      e.modifiers.every(
        m =>
          isObj(m) &&
          typeof m.token === 'string' &&
          typeof m.label === 'string' &&
          isObj(m.set) &&
          Object.values(m.set).every(v => typeof v === 'string') &&
          optString(m.group)
      ) &&
      (e.price === undefined ||
        (isObj(e.price) && optString(e.price.min) && optString(e.price.max)))
  );
}

export function saveCustomEngines(defs: CustomEngineDef[]): boolean {
  return write(ENGINES_KEY, defs);
}

export function loadGroups(): Group[] {
  const stored = read(GROUPS_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (g): g is Group =>
      isObj(g) &&
      typeof g.name === 'string' &&
      Array.isArray(g.members) &&
      g.members.every(m => typeof m === 'string') &&
      optString(g.description)
  );
}

export function saveGroups(groups: Group[]): boolean {
  return write(GROUPS_KEY, groups);
}

export interface RecentSearch {
  q: string;
  at: number;
}

export function loadRecent(): RecentSearch[] {
  const stored = read(RECENT_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (r): r is RecentSearch =>
      !!r && typeof r.q === 'string' && typeof r.at === 'number'
  );
}

/** Adds a search to the front of the list (moving it if already there). */
export function recordRecent(q: string, now = Date.now()): void {
  const query = q.trim();
  if (!query) return;
  const next = [
    { q: query, at: now },
    ...loadRecent().filter(r => r.q !== query),
  ];
  write(RECENT_KEY, next.slice(0, RECENT_MAX));
}

export function saveRecent(list: RecentSearch[]): void {
  write(RECENT_KEY, list);
}
